import type React from 'react';
import { useEffect, useRef, useState } from 'react';
// Asset imports - bundler handles path resolution
import closeIcon from '~/static/assets/icons/landing-page/close-icon.svg';
import { TOS_PDF_URL } from '../../constants/common';
import { useTOS } from '../../contexts/SettingsContext/hooks/useTOS';

interface TosModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
  persistToBackend?: boolean;
}

const TOSModal: React.FC<TosModalProps> = ({
  isOpen,
  onClose,
  onAccept,
  persistToBackend = true,
}) => {
  const [isChecked, setIsChecked] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const { acceptTOS } = useTOS();

  // Reset checkbox when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsChecked(false);
    }
  }, [isOpen]);

  // Handle ESC key press
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleContinue = async () => {
    if (!isChecked) {
      return;
    }

    setIsAccepting(true);
    try {
      if (persistToBackend) {
        await acceptTOS();
      }
      onAccept();
    } catch (error) {
      console.error('[TOSModal] Failed to accept TOS:', error);
      // Still allow user to continue - backend sync can happen later
      onAccept();
    } finally {
      setIsAccepting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="modal-overlay tos-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tos-modal-title"
    >
      <div ref={modalRef} className="tos-modal">
        {/* Modal Header */}
        <div className="modal-header">
          <h2 id="tos-modal-title" className="modal-title">
            Terms of service
          </h2>
          <button
            type="button"
            className="modal-close-button"
            onClick={onClose}
            aria-label="Close dialog"
          >
            <img
              src={closeIcon.src}
              width={closeIcon.width}
              height={closeIcon.height}
              alt=""
              className="close-icon"
              aria-hidden="true"
            />
          </button>
        </div>

        {/* Modal Content */}
        <div className="modal-content tos-modal-content">
          <p className="tos-description">
            To continue using Sangoma Meet, you must review and accept our Terms of Service (TOS).
            By checking the box, you are confirming our terms of service.
          </p>

          {/* Checkbox */}
          <div className="tos-checkbox-container">
            <label className="tos-checkbox-label">
              <input
                type="checkbox"
                checked={isChecked}
                onChange={(e) => setIsChecked(e.target.checked)}
                className="tos-checkbox"
                id="tos-agreement"
              />
              <span className="checkbox-custom" />
              <span className="checkbox-text">
                I agree to the{' '}
                <a
                  href={TOS_PDF_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tos-link"
                >
                  terms and conditions
                </a>
              </span>
            </label>
          </div>

          {/* Modal Actions */}
          <div className="modal-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={onClose}
              disabled={isAccepting}
            >
              CANCEL
            </button>
            <button
              type="button"
              className="continue-button"
              onClick={handleContinue}
              disabled={!isChecked || isAccepting}
            >
              {isAccepting ? 'ACCEPTING...' : 'CONTINUE'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { TOSModal };
